import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeftIcon, ChevronRightIcon, SearchIcon, FilterIcon, TicketIcon, FileDownIcon, CheckCircleIcon, XIcon, EyeIcon } from 'lucide-react';
import StatusBadge from '../common/StatusBadge';
import EmptyState from '../common/EmptyState';
import Tooltip from '../common/Tooltip';
import DateRangePicker from '../common/DateRangePicker';
import MultiSelect from '../common/MultiSelect';
import ExportModal from '../modals/ExportModal';
import ViewTicketDetailsModal from '../modals/ViewTicketDetailsModal';
import { format } from 'date-fns';
interface TicketsTabProps {
  showFilters: boolean;
  onToggleFilters: () => void;
}
// Mock tickets data
const mockTickets = [{
  id: 1,
  ticketNumber: '1601234567890',
  paxName: 'LOH/POHLEE MS',
  sector: 'SIN/CAI/SIN',
  sectorCode: 'SINCAI',
  airlineCode: 'QR',
  class: 'G',
  issueDate: '2023-06-15',
  airFare: 1200.0,
  tax: 300.0,
  totalCost: 1500.0,
  groupId: 'TG-2023-001',
  status: 'reconciled',
  reconciled: true,
  remarks: 'VIP passenger'
}, {
  id: 2,
  ticketNumber: '1601234567891',
  paxName: 'WONG/MARY MRS',
  sector: 'SIN/CAI/SIN',
  sectorCode: 'SINCAI',
  airlineCode: 'QR',
  class: 'G',
  issueDate: '2023-06-15',
  airFare: 1200.0,
  tax: 300.0,
  totalCost: 1500.0,
  groupId: 'TG-2023-001',
  status: 'pending',
  reconciled: false,
  remarks: ''
}, {
  id: 3,
  ticketNumber: '1601234567892',
  paxName: 'TAN/JOHN MR',
  sector: 'SIN/CAI/SIN',
  sectorCode: 'SINCAI',
  airlineCode: 'SQ',
  class: 'G',
  issueDate: '2023-06-18',
  airFare: 1500.0,
  tax: 300.0,
  totalCost: 1800.0,
  groupId: 'TG-2023-002',
  status: 'pending',
  reconciled: false,
  remarks: 'Frequent flyer',
  type: 'ATC',
  referenceNumber: '12XCAI1207',
  tourCode: '12XCAI1207/23QR',
  staffResponsible: 'Jane Smith'
}, {
  id: 4,
  ticketNumber: '1601234567893',
  paxName: 'CHEN/DAVID MR',
  sector: 'SIN/CAI/SIN',
  sectorCode: 'SINCAI',
  airlineCode: 'SQ',
  class: 'G',
  issueDate: '2023-06-19',
  airFare: 2000.0,
  tax: 400.0,
  totalCost: 2400.0,
  groupId: 'TG-2023-003',
  status: 'reconciled',
  reconciled: true,
  remarks: ''
}, {
  id: 5,
  ticketNumber: '1601234567894',
  paxName: 'SMITH/JANE MRS',
  sector: 'SIN/CAI/SIN',
  sectorCode: 'SINCAI',
  airlineCode: 'EK',
  class: 'G',
  issueDate: '2023-06-20',
  airFare: 3000.0,
  tax: 500.0,
  totalCost: 3500.0,
  groupId: 'TG-2023-003',
  status: 'pending',
  reconciled: false,
  remarks: ''
}];
const TicketsTab: React.FC<TicketsTabProps> = ({
  showFilters,
  onToggleFilters
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    airlineCode: '',
    status: '',
    groupId: '',
    dateRange: {
      start: '',
      end: ''
    },
    airlineCodes: []
  });
  // Format date in DD MMM YYYY format
  const formatDate = dateString => {
    if (!dateString) return '';
    try {
      return format(new Date(dateString), 'dd MMM yyyy');
    } catch (e) {
      return dateString;
    }
  };
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };
  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };
  const clearFilters = () => {
    setFilters({
      airlineCode: '',
      status: '',
      groupId: '',
      dateRange: {
        start: '',
        end: ''
      },
      airlineCodes: []
    });
  };
  const handleViewTicket = ticket => {
    setSelectedTicket(ticket);
    setIsViewModalOpen(true);
  };
  // Filter tickets based on search term and filters
  const filteredTickets = mockTickets.filter(ticket => {
    // Search term filter
    const matchesSearch = ticket.ticketNumber.toLowerCase().includes(searchTerm.toLowerCase()) || ticket.paxName.toLowerCase().includes(searchTerm.toLowerCase()) || ticket.groupId.toLowerCase().includes(searchTerm.toLowerCase());
    // Additional filters
    const matchesAirline = filters.airlineCode ? ticket.airlineCode === filters.airlineCode : true;
    const matchesStatus = filters.status ? ticket.status === filters.status : true;
    const matchesGroup = filters.groupId ? ticket.groupId.includes(filters.groupId) : true;
    return matchesSearch && matchesAirline && matchesStatus && matchesGroup;
  });
  // Sort tickets
  let sortedTickets = [...filteredTickets];
  if (sortColumn) {
    sortedTickets.sort((a, b) => {
      const aValue = a[sortColumn];
      const bValue = b[sortColumn];
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      }
      return sortDirection === 'asc' ? aValue > bValue ? 1 : -1 : aValue < bValue ? 1 : -1;
    });
  }
  // Calculate totals
  const totals = {
    airFare: filteredTickets.reduce((sum, ticket) => sum + ticket.airFare, 0),
    tax: filteredTickets.reduce((sum, ticket) => sum + ticket.tax, 0),
    totalCost: filteredTickets.reduce((sum, ticket) => sum + ticket.totalCost, 0)
  };
  return <div className="space-y-6" data-id="element-1129">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-1130">
        <h2 className="text-xl font-medium text-gray-900" data-id="element-1131">
          All Tickets Records
        </h2>
        <div className="flex flex-wrap gap-2" data-id="element-1132">
          <button className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" onClick={() => setIsExportModalOpen(true)} aria-label="Export ticket data" data-id="element-1133">
            <FileDownIcon className="h-5 w-5 mr-1" data-id="element-1134" />
            <span data-id="element-1135">Export</span>
          </button>
          <button className={`inline-flex items-center px-4 py-2 border ${showFilters ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-300 text-gray-700'} rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`} onClick={onToggleFilters} aria-expanded={showFilters} aria-controls="filter-panel" data-id="element-1136">
            <FilterIcon className="h-5 w-5 mr-1" data-id="element-1137" />
            <span data-id="element-1138">Filter</span>
          </button>
        </div>
      </div>
      <div className="bg-white rounded-lg shadow overflow-hidden" data-id="element-1139">
        <div className="p-4 border-b border-gray-200" data-id="element-1140">
          <div className="relative" data-id="element-1141">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1142">
              <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-1143" />
            </div>
            <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Search by ticket number, passenger name, or group ID..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} aria-label="Search tickets" data-id="element-1144" />
          </div>
        </div>
        {showFilters && <div className="p-4 border-b border-gray-200 bg-gray-50" data-id="element-1145">
            <div className="flex justify-between items-center mb-4" data-id="element-1146">
              <h2 className="text-sm font-medium text-gray-700" data-id="element-1147">
                Filter Tickets
              </h2>
              <button className="text-sm text-blue-600 hover:text-blue-800" onClick={clearFilters} data-id="element-1148">
                Clear Filters
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1149">
              <div data-id="element-1150">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1151">
                  Airline Code
                </label>
                <MultiSelect options={[{
              value: 'SQ',
              label: 'SQ - Singapore Airlines'
            }, {
              value: 'QR',
              label: 'QR - Qatar Airways'
            }, {
              value: 'EK',
              label: 'EK - Emirates'
            }, {
              value: 'CX',
              label: 'CX - Cathay Pacific'
            }, {
              value: 'MH',
              label: 'MH - Malaysia Airlines'
            }]} value={filters.airlineCodes} onChange={value => handleFilterChange('airlineCodes', value)} placeholder="Select airlines..." data-id="element-1152" />
              </div>
              <div data-id="element-1153">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1154">
                  Status
                </label>
                <select name="status" value={filters.status} onChange={e => handleFilterChange('status', e.target.value)} className="block w-full px-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-1155">
                  <option value="" data-id="element-1156">All Statuses</option>
                  <option value="reconciled" data-id="element-1157">Reconciled</option>
                  <option value="pending" data-id="element-1158">Pending</option>
                </select>
              </div>
              <div data-id="element-1159">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1160">
                  Group ID
                </label>
                <input type="text" name="groupId" value={filters.groupId} onChange={e => handleFilterChange('groupId', e.target.value)} className="block w-full px-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Enter group ID..." data-id="element-1161" />
              </div>
              <div data-id="element-1162">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1163">
                  Issue Date Range
                </label>
                <DateRangePicker value={filters.dateRange} onChange={value => handleFilterChange('dateRange', value)} data-id="element-1164" />
              </div>
            </div>
            <div className="flex justify-end pt-4" data-id="element-1165">
              <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" onClick={() => {
            // Apply filters logic here
            console.log('Applied filters:', filters);
          }} data-id="element-1166">
                Apply Filters
              </button>
            </div>
          </div>}
        {sortedTickets.length > 0 ? <div className="overflow-x-auto" data-id="element-1167">
            <table className="min-w-full divide-y divide-gray-200" aria-label="Tickets list" data-id="element-1168">
              <thead className="bg-gray-50 sticky top-0 z-10" data-id="element-1169">
                <tr data-id="element-1170">
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('ticketNumber')} aria-sort={sortColumn === 'ticketNumber' ? sortDirection : undefined} data-id="element-1171">
                    <div className="flex items-center" data-id="element-1172">
                      Ticket Number
                      {sortColumn === 'ticketNumber' && <span className="ml-1" data-id="element-1173">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>}
                    </div>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('paxName')} aria-sort={sortColumn === 'paxName' ? sortDirection : undefined} data-id="element-1174">
                    <div className="flex items-center" data-id="element-1175">
                      Passenger Name
                      {sortColumn === 'paxName' && <span className="ml-1" data-id="element-1176">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>}
                    </div>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('sector')} aria-sort={sortColumn === 'sector' ? sortDirection : undefined} data-id="element-1177">
                    <div className="flex items-center" data-id="element-1178">
                      Sector
                      {sortColumn === 'sector' && <span className="ml-1" data-id="element-1179">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>}
                    </div>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('airlineCode')} aria-sort={sortColumn === 'airlineCode' ? sortDirection : undefined} data-id="element-1180">
                    <div className="flex items-center" data-id="element-1181">
                      A/L Code
                      {sortColumn === 'airlineCode' && <span className="ml-1" data-id="element-1182">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>}
                    </div>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('class')} aria-sort={sortColumn === 'class' ? sortDirection : undefined} data-id="element-1183">
                    <div className="flex items-center" data-id="element-1184">
                      Class
                      {sortColumn === 'class' && <span className="ml-1" data-id="element-1185">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>}
                    </div>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('issueDate')} aria-sort={sortColumn === 'issueDate' ? sortDirection : undefined} data-id="element-1186">
                    <div className="flex items-center" data-id="element-1187">
                      Issue Date
                      {sortColumn === 'issueDate' && <span className="ml-1" data-id="element-1188">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>}
                    </div>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('tax')} aria-sort={sortColumn === 'tax' ? sortDirection : undefined} data-id="element-1189">
                    <div className="flex items-center" data-id="element-1190">
                      Tkt Tax
                      {sortColumn === 'tax' && <span className="ml-1" data-id="element-1191">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>}
                    </div>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('airFare')} aria-sort={sortColumn === 'airFare' ? sortDirection : undefined} data-id="element-1192">
                    <div className="flex items-center" data-id="element-1193">
                      Air Fare
                      {sortColumn === 'airFare' && <span className="ml-1" data-id="element-1194">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>}
                    </div>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('totalCost')} aria-sort={sortColumn === 'totalCost' ? sortDirection : undefined} data-id="element-1195">
                    <div className="flex items-center" data-id="element-1196">
                      Total Cost
                      {sortColumn === 'totalCost' && <span className="ml-1" data-id="element-1197">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>}
                    </div>
                  </th>
                  <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('reconciled')} aria-sort={sortColumn === 'reconciled' ? sortDirection : undefined} data-id="element-1198">
                    <div className="flex items-center justify-center" data-id="element-1199">
                      Recon
                      {sortColumn === 'reconciled' && <span className="ml-1" data-id="element-1200">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>}
                    </div>
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1201">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-1202">
                {sortedTickets.map((ticket, index) => <tr key={ticket.id} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 ${ticket.type === 'ATC' ? 'bg-amber-50 hover:bg-amber-100' : ''}`} data-id="element-1203">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1204">
                      {ticket.ticketNumber}
                      {ticket.type === 'ATC' && <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800" data-id="element-1205">
                          ATC
                        </span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1206">
                      {ticket.paxName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1207">
                      {ticket.sector}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1208">
                      {ticket.airlineCode}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1209">
                      {ticket.class}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1210">
                      {formatDate(ticket.issueDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1211">
                      $
                      {ticket.tax.toLocaleString('en-US', {
                  minimumFractionDigits: 2
                })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1212">
                      $
                      {ticket.airFare.toLocaleString('en-US', {
                  minimumFractionDigits: 2
                })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1213">
                      $
                      {ticket.totalCost.toLocaleString('en-US', {
                  minimumFractionDigits: 2
                })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center" data-id="element-1214">
                      {ticket.reconciled ? <CheckCircleIcon className="h-5 w-5 text-green-500 mx-auto" data-id="element-1215" /> : <XIcon className="h-5 w-5 text-red-500 mx-auto" data-id="element-1216" />}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-1217">
                      <Tooltip content="View ticket details" data-id="element-1218">
                        <button onClick={() => handleViewTicket(ticket)} className="text-blue-600 hover:text-blue-900 mr-2" aria-label={`View ticket ${ticket.ticketNumber} details`} data-id="element-1219">
                          <EyeIcon className="h-4 w-4 inline" data-id="element-1220" />
                        </button>
                      </Tooltip>
                    </td>
                  </tr>)}
              </tbody>
              <tfoot className="bg-gray-100" data-id="element-1221">
                <tr data-id="element-1222">
                  <td colSpan={6} className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right" data-id="element-1223">
                    Summary Totals:
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1224">
                    $
                    {totals.tax.toLocaleString('en-US', {
                  minimumFractionDigits: 2
                })}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1225">
                    $
                    {totals.airFare.toLocaleString('en-US', {
                  minimumFractionDigits: 2
                })}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1226">
                    $
                    {totals.totalCost.toLocaleString('en-US', {
                  minimumFractionDigits: 2
                })}
                  </td>
                  <td colSpan={2} data-id="element-1227"></td>
                </tr>
              </tfoot>
            </table>
          </div> : <EmptyState title="No tickets found" description="Try adjusting your search or filter criteria" icon={<TicketIcon className="h-8 w-8 text-gray-400" data-id="element-1229" />} actionLabel="Create New Ticket" actionLink="/create-group" data-id="element-1228" />}
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6" data-id="element-1230">
          <div className="flex-1 flex justify-between sm:hidden" data-id="element-1231">
            <button className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" aria-label="Previous page" data-id="element-1232">
              Previous
            </button>
            <button className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" aria-label="Next page" data-id="element-1233">
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between" data-id="element-1234">
            <div data-id="element-1235">
              <p className="text-sm text-gray-700" data-id="element-1236">
                Showing <span className="font-medium" data-id="element-1237">1</span> to{' '}
                <span className="font-medium" data-id="element-1238">{sortedTickets.length}</span> of{' '}
                <span className="font-medium" data-id="element-1239">{mockTickets.length}</span>{' '}
                results
              </p>
            </div>
            <div data-id="element-1240">
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination" data-id="element-1241">
                <button className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50" aria-label="Previous page" data-id="element-1242">
                  <span className="sr-only" data-id="element-1243">Previous</span>
                  <ChevronLeftIcon className="h-5 w-5" data-id="element-1244" />
                </button>
                <button className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50" aria-label="Page 1" aria-current="page" data-id="element-1245">
                  1
                </button>
                <button className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50" aria-label="Next page" data-id="element-1246">
                  <span className="sr-only" data-id="element-1247">Next</span>
                  <ChevronRightIcon className="h-5 w-5" data-id="element-1248" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      </div>
      {isExportModalOpen && <ExportModal onClose={() => setIsExportModalOpen(false)} data-id="element-1249" />}
      {isViewModalOpen && selectedTicket && <ViewTicketDetailsModal onClose={() => {
      setIsViewModalOpen(false);
      setSelectedTicket(null);
    }} ticket={selectedTicket} data-id="element-1250" />}
    </div>;
};
export default TicketsTab;